"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Slider,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  Avatar,
  TextField,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Person } from "@mui/icons-material"
import { apiRemote, type ISubmissionTcc } from "@/api"
import { useUser } from "@/contexts/UserContext"
import { useAlert } from "@/contexts/AlertContext"
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext"

interface ScoringCriterion {
  id: string
  name: string
  maxScore: number
  description: string
  levels: {
    name: string
    range: string
    criteria: string[]
  }[]
}

interface ScoringFormProps {
  contestant: ISubmissionTcc["data"]["data"]
  criteria: ScoringCriterion[]
  onSubmit: (scores: Record<string, number>) => void
  onBack: () => void
  existingScores?: Record<string, number>
  handleNext: () => void
}

const keyMappings: Record<string, string> = {
  problem: "Problem",
  schoolType: "School Type",
  location: "Location",
  studentPopulation: "Student Population",
  teacherCount: "Teacher Count",
  techResources: "Technology Resources",
  keyChallenges: "Key Challenges",
  additionalContext: "Additional Context",
  aiPrompt: "AI Prompt",
  aiTool: "AI Tool",
  aiEngine: "AI Engine",
  aiFinalProposal: "Ai Proposal",
  finalProposal: "Final Proposal",
  critique: "Critique",
  strengths: "Strengths",
  weaknesses: "Weaknesses",
  feasibility: "Feasibility",
  ethics: "Ethics",
  implementationSteps: "Implementation Steps",
  resourcesNeeded: "Resources Needed",
  expectedOutcomes: "Expected Outcomes",
}

// Define the scoring criteria based on the rubric
const scoringRubric: ScoringCriterion[] = [
  {
    id: "relevance",
    name: "Relevance & Problem Clarity",
    maxScore: 40,
    description: "This criterion evaluates the clarity and relevance of the stated problem, ensuring it addresses a genuine and pressing educational challenge in the AI era, supported by data and literature.",
    levels: [
      {
        name: "Advanced",
        range: "31-40",
        criteria: [
          "Problem statement is outstandingly to supremely clear, concise, and well-defined, with a compelling narrative and evidence of original thought.",
          "Challenges addressed are extremely relevant to the AI era or educational context.",
          "Provides excellent supporting data.",
          "Displays an excellent, thorough, and critically analyzed literature review."
        ],
      },
      {
        name: "Proficient",
        range: "21-30",
        criteria: [
          "Problem statement is exceptionally to remarkably clear, concise, and well-defined.",
          "Challenges addressed are highly to extremely relevant to the AI era or educational context.",
          "Presents relevant to ample supporting data.",
          "Displays limited to solid base of relevant literature review."
        ],
      },
      {
        name: "Developing",
        range: "11-20",
        criteria: [
          "Problem statement is clear but may lack some detail, depth, or nuance.",
          "Challenges addressed have some to high relevance to the AI era or educational context.",
          "May begin to show evidence of relevant data, but no literature review."
        ],
      },
      {
        name: "Hardly Evident",
        range: "1-10",
        criteria: [
          "Problem statement is vague, incoherent, or completely absent.",
          "Demonstrates minimal to no relevance to the AI era or educational context.",
          "Lacks any supporting data or literature review."
        ],
      },
    ],
  },
  {
    id: "innovation",
    name: "Innovation & Practicality",
    maxScore: 40,
    description: "This criterion assesses the creativity and feasibility of the proposed solution within a typical school setting, as well as its alignment with 21st-century learning principles.",
    levels: [
      {
        name: "Advanced",
        range: "31-40",
        criteria: [
          "Solution showcases exceptional to revolutionary creativity and practicality, with transformative potential within a typical school setting.",
          "Feasibility is outstanding to maximized, with well-articulated implementation strategies and resource optimization.",
          "Aligns exceptionally with a broad range of 21st-century learning principles, including adaptability, self-direction, entrepreneurship, civic engagement, and environmental awareness."
        ],
      },
      {
        name: "Proficient",
        range: "21-30",
        criteria: [
          "Solution exhibits clear to excellent creativity and demonstrates strong practicality within a school context.",
          "Feasibility is well-supported to highly robust, with realistic implementation strategies and comprehensive resource planning.",
          "Aligns effectively to excellently with core 21st-century learning principles, including critical thinking, collaboration, communication, creativity, and problem-solving."
        ],
      },
      {
        name: "Developing",
        range: "11-20",
        criteria: [
          "Solution shows emerging to moderate creativity and identifies some to key practical considerations.",
          "Feasibility is uncertain to reasonable, with potential challenges not fully addressed.",
          "Connection to 21st-century learning principles is basic to evident but may be limited in scope."
        ],
      },
      {
        name: "Hardly Evident",
        range: "1-10",
        criteria: [
          "Solution lacks discernible creativity or exhibits minimal originality, relying on commonplace ideas.",
          "Demonstrates fundamental impracticality or presents significant obstacles to implementation in a school setting.",
          "Shows no connection to or superficial understanding of 21st-century learning principles."
        ],
      },
    ],
  },
  {
    id: "critique",
    name: "Critique Depth",
    maxScore: 40,
    description: "This criterion assesses the depth and quality of the analysis of the AI-generated proposal's strengths and weaknesses, including the specificity of examples and the thoughtfulness of insights.",
    levels: [
      {
        name: "Advanced",
        range: "31-40",
        criteria: [
          "Critique showcases exceptional to groundbreaking analysis with nuanced to revolutionary insights.",
          "Provides multiple detailed to exhaustive examples and expert to visionary critique.",
          "Demonstrates a sophisticated understanding of the proposal's complexities and shows a unique perspective."
        ],
      },
      {
        name: "Proficient",
        range: "21-30",
        criteria: [
          "Critique provides a clear to excellent analysis of strengths and weaknesses with well-supported evidence.",
          "Uses relevant to comprehensive examples and evidence to justify claims.",
          "Demonstrates a solid to comprehensive understanding of the proposal's context and goes beyond surface-level observations."
        ],
      },
      {
        name: "Developing",
        range: "11-20",
        criteria: [
          "Critique provides basic to more detailed analysis, identifying some to several strengths and weaknesses.",
          "Examples are limited to specific, but may lack full development.",
          "Analysis demonstrates a moderate understanding of the proposal with insights that are present but not fully developed."
        ],
      },
      {
        name: "Hardly Evident",
        range: "1-10",
        criteria: [
          "Critique demonstrates a complete lack of analysis to superficial observations.",
          "Fails to identify strengths or weaknesses or mentions them in a cursory manner.",
          "Analysis is minimal to general and lacks supporting details or critical thought."
        ],
      },
    ],
  },
  {
    id: "refinement",
    name: "Refinement Quality",
    maxScore: 60,
    description: "This criterion assesses the quality of the final proposal, focusing on the degree of thoughtful human intervention, contextual adaptation, the rigor of supporting data and literature review, the clarity and effectiveness of the methodology and solutions, and the overall writing quality.",
    levels: [
      {
        name: "Advanced",
        range: "46-60",
        criteria: [
          "Proposal showcases exceptional to revolutionary human intervention and contextual adaptation.",
          "Includes outstanding to groundbreaking supporting data and/or literature review, demonstrating deep understanding and original thought.",
          "Methodology and solutions are sophisticated to visionary, innovative, and well-articulated with potential for significant impact.",
          "Writing is sophisticated to expertly crafted, demonstrating exceptional clarity, precision, and command of language and style."
        ],
      },
      {
        name: "Proficient",
        range: "31-45",
        criteria: [
          "Proposal demonstrates clear to strong human intervention and contextual adaptation.",
          "Provides strong to thorough supporting data and/or literature review that effectively supports the proposal.",
          "Methodology and solutions are well-written to well-organized, logical, feasible, and persuasive.",
          "Writing is well-organized, clear, professional, and demonstrates strong command of language."
        ],
      },
      {
        name: "Developing",
        range: "16-30",
        criteria: [
          "Proposal shows emerging to developing human intervention and contextual adaptation.",
          "Provides some to good supporting data and/or literature review, with varying relevance.",
          "Methodology and solutions are basic to clearly presented but may lack thoroughness.",
          "Writing is improving in clarity and organization but may still lack precision in some areas."
        ],
      },
      {
        name: "Hardly Evident",
        range: "1-15",
        criteria: [
          "Proposal shows no to minimal human intervention and superficial contextual adaptation.",
          "Lacks any to provides limited supporting data and/or literature review.",
          "Methodology and solutions are absent to poorly defined and lack coherence.",
          "Writing is extremely poor to lacking in clarity, with significant errors and poor organization."
        ],
      },
    ],
  },
  {
    id: "impact",
    name: "Overall Impact",
    maxScore: 20,
    description: "This criterion evaluates the potential of the proposed solution to transform classrooms, improve learning outcomes, and inspire further innovation in education.",
    levels: [
      {
        name: "Advanced",
        range: "16-20",
        criteria: [
          "Solution showcases exceptional to revolutionary potential to transform classrooms, improve learning outcomes, and inspire groundbreaking innovation.",
          "Impact is significant, far-reaching, and has the potential to reshape educational practices.",
          "Demonstrates a high degree of originality with transformative impact."
        ],
      },
      {
        name: "Proficient",
        range: "11-15",
        criteria: [
          "Solution demonstrates clear to strong potential to improve learning outcomes, transform classrooms, and inspire innovation.",
          "Shows transformative elements within classroom environments with a clear path for positive impact.",
          "Impact is well-defined and supported."
        ],
      },
      {
        name: "Developing",
        range: "6-10",
        criteria: [
          "Solution shows some to moderate potential to enhance learning outcomes and inspire some innovation.",
          "Impact may be limited by certain factors or scope.",
          "Includes some innovative elements that suggest future potential."
        ],
      },
      {
        name: "Hardly Evident",
        range: "1-5",
        criteria: [
          "Solution demonstrates no to minimal potential for positive impact on classrooms or learning outcomes.",
          "Lacks relevance or applicability to educational settings.",
          "Offers no discernible inspiration for further innovation."
        ],
      },
    ],
  },
]

export default function ScoringForm({
  contestant,
  criteria,
  onSubmit,
  onBack,
  existingScores,
  handleNext,
}: ScoringFormProps) {
  const [scores, setScores] = useState<Record<string, number>>({})
  const [totalScore, setTotalScore] = useState<number>(0)
  const [feedback, setFeedback] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const { user } = useUser()
  const alert = useAlert()
  const { loadTCCSubmission1, currentPageSubmission, pageSizeSubmission } = useThoughtCoCreate()

  // Initialize scores from existing scores or default to 0
  useEffect(() => {
    const initialScores: Record<string, number> = {}
    let userScore = 0
    let userFeedback = ""

    if (contestant.judge) {
      const userJudge = contestant.judge
      if (userJudge) {
        userScore = userJudge.score
        userFeedback = userJudge.feedback
      }
    }

    // Initialize score
    initialScores["score"] = userScore || 0

    setScores(initialScores)
    setFeedback(userFeedback || "")

    // Set total score
    setTotalScore(userScore || 0)
  }, [contestant])

  const handleScoreChange = (criterionId: string, value: number) => {
    const newScores = { ...scores, [criterionId]: value }
    setScores(newScores)
    setTotalScore(value) // Update total score directly from the slider value
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const data = await apiRemote.saveGrade({
        user: contestant.user,
        judge: user?.user_login || "",
        score: scores.score,
        feedback: feedback,
      })

      if (data.code == 200) {
        loadTCCSubmission1(currentPageSubmission, pageSizeSubmission)
          .then((data) => {
            handleNext()
            setLoading(false)
          })
          .catch((e) => {
            console.error(e)
          })
      } else {
        alert.error(data.message)
        setLoading(false)
      }
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  }

  const handleFeedbackChange = (value: string) => {
    setFeedback(value)
  }

  // Get the level description based on the score
  const getLevelDescription = (score: number): string => {
    if (score >= 170) return "Advanced"
    if (score >= 110) return "Proficient"
    if (score >= 60) return "Developing"
    if (score > 0) return "Hardly Evident"
    return "Not Scored"
  }

  const maxPossibleScore = process.env.MAX_SCORE || 200 // Total possible score

  return (
    <Box>
      <Button variant="outlined" onClick={onBack} sx={{ mb: 3 }}>
        Back to Contestants
      </Button>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4} sx={{ height: "100%" }}>
          <Card sx={{ height: "100%" }}>
            <Box
              sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 140, bgcolor: "grey.200" }}
            >
              <Avatar sx={{ width: 80, height: 80 }}>
                <Person fontSize="large" />
              </Avatar>
            </Box>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {contestant.display_name}
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                Total Score: {totalScore} / {maxPossibleScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contestant.submitted_count} of {contestant.total_parts} deliverables completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Scoring
            </Typography>

            {/* Original scoring slider */}
            <Box sx={{ mb: 4 }}>
              <Typography id="score-slider" gutterBottom>
                Score: {scores.score || 0} / {maxPossibleScore}
              </Typography>
              <Slider
                value={scores.score || 0}
                onChange={(_, value) => handleScoreChange("score", value as number)}
                aria-labelledby="score-slider"
                valueLabelDisplay="auto"
                step={1}
                marks
                min={0}
                max={maxPossibleScore}
              />
              <Typography variant="body2" color="text.secondary">
                {getLevelDescription(scores.score || 0)}
              </Typography>
            </Box>

            <TextField
              label="Feedback"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={feedback || ""}
              onChange={(e) => handleFeedbackChange(e.target.value)}
              sx={{ mt: 3 }}
              placeholder="Provide detailed feedback on the submission..."
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
              <Button variant="outlined" onClick={onBack}>
                Back
              </Button>
              <Button variant="contained" color="primary" onClick={handleSubmit} size="large" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : null}
                Submit Scores & Feedback
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Rubric Reference Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Scoring Rubric Reference
            </Typography>

            {scoringRubric.map((criterion) => (
              <Accordion key={criterion.id} sx={{ mb: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${criterion.id}-content`}
                  id={`${criterion.id}-header`}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {criterion.name} ({criterion.maxScore} points)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {criterion.description}
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Level</TableCell>
                          <TableCell>Score Range</TableCell>
                          <TableCell>Criteria</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {criterion.levels.map((level) => (
                          <TableRow key={level.name}>
                            <TableCell component="th" scope="row">
                              <Typography variant="body2" fontWeight="medium">
                                {level.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{level.range}</TableCell>
                            <TableCell>
                              <ul style={{ margin: 0, paddingLeft: "1rem" }}>
                                {level.criteria.map((item, index) => (
                                  <li key={index}>
                                    <Typography variant="body2">{item}</Typography>
                                  </li>
                                ))}
                              </ul>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>

        {/* Submissions Display */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Submissions
            </Typography>
            {contestant?.submissions?.map((submission) => (
              <Box key={submission.round_part_id} sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: "8px" }}>
                <Typography variant="h6">{submission.section}</Typography>
                <Box sx={{ whiteSpace: "pre-wrap", background: "#f5f5f5", padding: "10px", borderRadius: "5px" }}>
                  {Object.entries(submission.content).map(([key, value]) => (
                    <Typography key={key} variant="body2" paragraph>
                      <strong>{keyMappings[key] || key}:</strong>{" "}
                      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

